import * as analytics from '../commons/testimAnalytics';
import { isCi } from '../cli/isCiRun';
import type { getLogger } from '../commons/logger';
import type { LightweightSettings } from '../runOptions';
import type { CompanyPlanTypes } from 'services/src/commons/utils';
import type { ProjectType } from 'services/src/commons/mongo/types/DbProject';

const calcSource = (source: string, user?: string) => {
    if (source !== 'cli' && source !== 'cli-local') {
        return source;
    }

    if (isCi && user) {
        return 'ci-with-user';
    }

    if (isCi) {
        return 'ci';
    }

    if (user) {
        return 'cli-with-user';
    }

    return source;
};

function setLightweightAnalytics(properties: { [key: string]: any }, lightweightMode?: LightweightSettings) {
    if (lightweightMode?.type) {
        properties[`${lightweightMode.type}Mode`] = true;
    }
    return properties;
}

export function analyticsTestStart({
    executionId,
    projectId,
    testId,
    resultId,
    ucid,
    companyId,
    companyName,
    projectName,
    companyPlan,
    source,
    user,
    lightweightMode,
    isStartUp,
    projectType,
    appSource,
    gridType,
}: Omit<AnalyticsParams, 'result' | 'logger'>) {
    const properties = setLightweightAnalytics({
        executionId,
        projectId,
        testId,
        resultId,
        ucid,
        companyId,
        companyName,
        projectName,
        companyPlan,
        source: calcSource(source, user),
        isStartUp,
        projectType,
        gridType,
        ...(['android', 'ios'].includes(projectType!) && appSource && { appSource }),
    }, lightweightMode);
    analytics.trackWithCIUser('test-run-ci', properties);
}

interface AnalyticsParams {
    executionId: string;
    projectId: string;
    testId: string;
    resultId: string;
    result: any;
    ucid: string;
    companyId?: string;
    companyName?: string;
    projectName?: string;
    companyPlan?: CompanyPlanTypes;
    source: string;
    user?: string;
    lightweightMode?: LightweightSettings;
    logger: ReturnType<typeof getLogger>;
    isStartUp?: boolean;
    projectType?: ProjectType;
    appSource?: string;
    gridType?: string;
}
export function analyticsTestEnd({
    executionId,
    projectId,
    testId,
    resultId,
    result,
    ucid,
    companyId,
    companyName,
    projectName,
    companyPlan,
    source,
    user,
    lightweightMode,
    logger,
    isStartUp,
    projectType,
    appSource,
    gridType,
}: AnalyticsParams) {
    try {
        const properties = setLightweightAnalytics({
            executionId,
            projectId,
            testId,
            resultId,
            ucid,
            companyId,
            companyName,
            projectName,
            companyPlan,
            mockNetworkEnabled: result.wasMockNetworkActivated,
            source: calcSource(source, user),
            isStartUp,
            projectType,
            gridType,
            ...(['android', 'ios'].includes(projectType!) && appSource && { appSource }),
        }, lightweightMode);

        if (result.success) {
            analytics.trackWithCIUser('test-run-ci-success', properties);
            return;
        }
        analytics.trackWithCIUser('test-run-ci-fail', { ...properties, failureReason: result.failureReason });
    } catch (err) {
        logger.error('failed to update test end analytics', { err });
    }
}

export function analyticsExecsStart(analyticsProperties: { executionId: string; projectId: string }) {
    analytics.trackWithCIUser('batch-run-ci', analyticsProperties);
}
